import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useConfirmRefresh, usePreviewRefresh } from '@/features/doer/use-doer'
import type { RefreshDiff, SnapshotStepBrief } from '@/api/types'

interface RefreshSnapshotDialogProps {
  projectId: string
  outOfDate: boolean
  trigger: React.ReactNode
}

const renderBriefList = (
  label: string,
  items: SnapshotStepBrief[],
  emptyHint: string
): React.ReactNode => (
  <section className="space-y-1">
    <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</h4>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground">{emptyHint}</p>
    ) : (
      <ul className="space-y-0.5 text-sm">
        {items.map((s) => (
          <li key={s.stepId} className="flex items-baseline justify-between gap-2">
            <span className="truncate">{s.title || '(untitled)'}</span>
            <span className="text-xs text-muted-foreground">{s.kind}</span>
          </li>
        ))}
      </ul>
    )}
  </section>
)

/**
 * Two-phase refresh: opening the dialog kicks off a preview fetch; confirming runs the replace.
 * Imprecision is acceptable in the diff (per planning doc): we render added/removed/modified by
 * step id + kind/title/parent change. Material-quantity drift isn't surfaced — pricing is Live so
 * BOM totals already reflect it.
 */
export const RefreshSnapshotDialog = ({
  projectId,
  outOfDate,
  trigger,
}: RefreshSnapshotDialogProps) => {
  const [open, setOpen] = useState(false)
  const [diff, setDiff] = useState<RefreshDiff | null>(null)
  const preview = usePreviewRefresh()
  const confirm = useConfirmRefresh()
  const previewMutate = preview.mutate

  useEffect(() => {
    if (!open) {
      setDiff(null)
      return
    }
    previewMutate(projectId, {
      onSuccess: (data) => setDiff(data),
      onError: () => toast.error('Could not preview refresh'),
    })
  }, [open, projectId, previewMutate])

  const handleConfirm = (): void => {
    confirm.mutate(projectId, {
      onSuccess: () => {
        toast.success('Refreshed from source')
        setOpen(false)
      },
      onError: () => toast.error('Refresh failed'),
    })
  }

  const noChanges =
    diff !== null &&
    diff.addedSteps.length === 0 &&
    diff.removedSteps.length === 0 &&
    diff.modifiedSteps.length === 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refresh from source</DialogTitle>
          <DialogDescription>
            {outOfDate
              ? 'The source project has changed since you started. Confirm to replace your snapshot.'
              : 'Pull the latest structural changes from the source project.'}
          </DialogDescription>
        </DialogHeader>

        {preview.isPending || diff === null ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : noChanges ? (
          <p className="text-sm text-muted-foreground">
            No structural changes to apply. Pricing is always live — material price changes are
            already reflected in your BOM.
          </p>
        ) : (
          <div className="space-y-3">
            {renderBriefList('Added', diff.addedSteps, 'Nothing added.')}
            {renderBriefList('Removed', diff.removedSteps, 'Nothing removed.')}
            {renderBriefList('Modified', diff.modifiedSteps, 'Nothing modified.')}
            <p className="pt-2 text-xs text-muted-foreground">
              Your progress is preserved for steps that still exist. Progress on removed steps stays
              in your history but is hidden from the view.
              {diff.removedSteps.length > 0 && (
                <>
                  {' '}
                  Phase 13: any dependencies pointing at the removed steps will be cleared
                  automatically.
                </>
              )}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={confirm.isPending || diff === null}>
            {confirm.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
